import { useEffect, useMemo, useState } from "react";
import SoleReceiptEditDialog from "@/components/SoleReceiptEditDialog";
import SoleCards from "@/components/SoleCards";
import { api, SIZES } from "@/lib/api";
import { useData } from "@/lib/data";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SizeMatrix from "@/components/SizeMatrix";
import { Layers, Plus, X, ClipboardList, Pencil } from "lucide-react";
import { toast } from "sonner";

const soleKey = (code, color) => `${code}||${color || ""}`;
const emptyLine = () => ({ id: Math.random().toString(36).slice(2), sole: "", sizes: {} });

export default function Soles() {
  const { products, soleCards } = useData();
  const [stock, setStock] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [soleOrders, setSoleOrders] = useState([]);
  const orderFor = (r) => (r?.sole_order_id ? soleOrders.find((o) => o.id === r.sole_order_id) : null);
  const [supplier, setSupplier] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const [s, r, so] = await Promise.all([api.get("/soles/stock"), api.get("/soles/receipts"), api.get("/sole-orders")]);
    setSoleOrders(so.data);
    setStock(s.data); setReceipts(r.data);
  };
  useEffect(() => { load(); }, []);

  // Sole cards (with models using them); receipts are entered against these
  const soles = useMemo(() => soleCards.map((c) => ({
    key: soleKey(c.sole_code, c.sole_color),
    code: c.sole_code,
    color: c.sole_color || "",
    models: [...new Set(products.filter((p) => soleKey(p.sole_code, p.sole_color) === soleKey(c.sole_code, c.sole_color)).map((p) => p.model_code))],
  })), [soleCards, products]);

  const rows = useMemo(() => {
    const all = [...soles];
    stock.forEach((s) => {
      const k = soleKey(s.sole_code, s.sole_color);
      if (!all.some((x) => x.key === k)) all.push({ key: k, code: s.sole_code, color: s.sole_color || "", models: [] });
    });
    return all.map((s) => {
      const sizes = {};
      let total = 0;
      stock.forEach((x) => {
        if (soleKey(x.sole_code, x.sole_color) === s.key) { sizes[x.size] = x.qty; total += x.qty; }
      });
      return { ...s, sizes, total };
    });
  }, [soles, stock]);

  const updateLine = (id, patch) => setLines((p) => p.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const totalPairs = lines.reduce((a, l) => a + Object.values(l.sizes).reduce((x, y) => x + (parseInt(y) || 0), 0), 0);

  const submit = async () => {
    const items = [];
    for (const l of lines) {
      const s = soles.find((x) => x.key === l.sole);
      if (!s) { toast.error("Her kalem için taban seçin"); return; }
      SIZES.forEach((size) => {
        const q = parseInt(l.sizes[size]) || 0;
        if (q > 0) items.push({ sole_code: s.code, sole_color: s.color, size, qty: q });
      });
    }
    if (!items.length) { toast.error("En az bir numara için adet giriniz"); return; }
    setSubmitting(true);
    try {
      await api.post("/soles/receipts", { supplier, note, items });
      toast.success(`${totalPairs} çift taban stoğa girildi`);
      setLines([emptyLine()]); setSupplier(""); setNote("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata oluştu"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="p-5 lg:p-10 max-w-[1400px]" data-testid="soles-page">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-widest text-[#B45309] mb-2">Taban Deposu</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight flex items-center gap-3">
            <Layers className="w-7 h-7 text-[#D97706]" /> Tabanlar
          </h1>
          <p className="text-[#6B5B4A] mt-2 text-sm">Gelen tabanları girin. Kesim girildiğinde stok kartına atanmış tabandan numara bazında otomatik düşer.</p>
        </div>
        <a href="/taban-siparis" target="_blank" rel="noreferrer" data-testid="sole-order-list-btn"
          className="inline-flex items-center gap-2 bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide h-11 px-5 rounded-md whitespace-nowrap">
          <ClipboardList className="w-4 h-4" /> Taban Sipariş Listesi
        </a>
      </div>

      <SoleCards onChanged={load} />

      {/* Stock matrix */}
      <div className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] overflow-x-auto mb-6" data-testid="sole-stock-matrix">
        <table className="w-full text-sm zs-table">
          <thead className="bg-[#EFE6D6] border-b border-[#DCCFB8]">
            <tr>
              <th className="text-left px-4 py-3">Taban Kodu</th>
              <th className="text-left px-4 py-3">Renk</th>
              <th className="text-left px-4 py-3">Modeller</th>
              {SIZES.map((s) => <th key={s} className="text-center px-2 py-3 w-14">N°{s}</th>)}
              <th className="text-right px-4 py-3">Kalan</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={14} className="px-4 py-10 text-center text-[#8C7B68]">Taban stoğu yok.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.key} className="border-b border-[#E8DDCA] hover:bg-[#EFE6D6]/50" data-testid={`sole-row-${r.key}`}>
                <td className="px-4 py-3 font-mono font-semibold text-[#B45309]">{r.code}</td>
                <td className="px-4 py-3">{r.color || "—"}</td>
                <td className="px-4 py-3 text-xs text-[#6B5B4A]">{r.models.join(", ") || "—"}</td>
                {SIZES.map((s) => {
                  const v = r.sizes[s] || 0;
                  const cls = v < 0 ? "text-[#EF4444] font-bold" : v === 0 ? "text-[#A8977F]" : v < 10 ? "text-[#B45309]" : "text-[#047857]";
                  return <td key={s} className={`text-center font-mono tabular-nums ${cls}`} data-testid={`sole-qty-${r.key}-${s}`}>{v}</td>;
                })}
                <td className={`px-4 py-3 text-right font-mono font-bold ${r.total < 0 ? "text-[#EF4444]" : ""}`} data-testid={`sole-total-${r.key}`}>{r.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Receipt form */}
      <div className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-5 mb-6" data-testid="sole-receipt-form">
        <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-4">Taban Girişi</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#6B5B4A]">Tedarikçi</Label>
            <Input data-testid="sole-supplier-input" value={supplier} onChange={(e) => setSupplier(e.target.value)}
              placeholder="Örn: Kaya Taban" className="mt-1 h-11 bg-[#EFE6D6] border-[#DCCFB8]" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#6B5B4A]">Not</Label>
            <Input data-testid="sole-note-input" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Opsiyonel" className="mt-1 h-11 bg-[#EFE6D6] border-[#DCCFB8]" />
          </div>
        </div>
        {lines.map((line, idx) => (
          <div key={line.id} className="rounded-md border border-[#DCCFB8] bg-[#EFE6D6] p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-mono uppercase tracking-widest text-[#B45309]">Kalem #{idx + 1}</span>
              {lines.length > 1 && (
                <button onClick={() => setLines((p) => p.filter((l) => l.id !== line.id))} data-testid={`sole-line-remove-${idx}`}
                  className="text-[#8C7B68] hover:text-[#EF4444]"><X className="w-4 h-4" /></button>
              )}
            </div>
            <Select value={line.sole} onValueChange={(v) => updateLine(line.id, { sole: v })}>
              <SelectTrigger data-testid={`sole-select-${idx}`} className="h-11 bg-[#FFFBF4] border-[#DCCFB8] mb-4">
                <SelectValue placeholder="Taban seçin..." />
              </SelectTrigger>
              <SelectContent className="bg-[#EFE6D6] border-[#DCCFB8] text-[#2B2118]">
                {soles.map((s) => (
                  <SelectItem key={s.key} value={s.key} data-testid={`sole-select-${idx}-option-${s.key}`}>
                    {s.code}{s.color ? ` · ${s.color}` : ""}{s.models.length ? ` (${s.models.join(", ")})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <SizeMatrix values={line.sizes} testIdPrefix={`sole-size-${idx}`}
              onChange={(s, v) => updateLine(line.id, { sizes: { ...line.sizes, [s]: v } })} />
          </div>
        ))}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <Button variant="outline" onClick={() => setLines((p) => [...p, emptyLine()])} data-testid="sole-add-line"
            className="border-[#DCCFB8] bg-[#EFE6D6] hover:bg-[#FFFBF4] text-[#B45309]">
            <Plus className="w-4 h-4 mr-2" /> Kalem Ekle
          </Button>
          <div className="flex items-center gap-4">
            <p className="font-mono text-2xl font-bold text-[#B45309]" data-testid="sole-receipt-total">{totalPairs} çift</p>
            <Button onClick={submit} disabled={submitting} data-testid="sole-submit-btn"
              className="bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide h-11 px-6">
              {submitting ? "Kaydediliyor..." : "Taban Girişi Yap"}
            </Button>
          </div>
        </div>
      </div>

      <SoleReceiptEditDialog receipt={editingReceipt} order={orderFor(editingReceipt)} onOpenChange={(v) => { if (!v) setEditingReceipt(null); }} onSaved={load} />
      <section className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-5" data-testid="sole-receipt-history">
        <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-4">Taban Giriş Geçmişi</h2>
        {receipts.length === 0 && <p className="text-sm text-[#8C7B68]">Henüz taban girişi yok</p>}
        <div className="space-y-3">
          {receipts.map((r) => (
            <div key={r.id} className="rounded-md border border-[#DCCFB8] bg-[#EFE6D6] p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-medium">{r.supplier || "Tedarikçi"}</p>
                  <p className="text-[11px] font-mono text-[#8C7B68] uppercase tracking-widest">{new Date(r.created_at).toLocaleString("tr-TR")}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xl font-bold text-[#B45309]">+{r.items.reduce((a, b) => a + b.qty, 0)} çift</span>
                  <button onClick={() => setEditingReceipt(r)} title="Düzenle" data-testid={`sole-receipt-edit-${r.id}`}
                    className="text-[#8C7B68] hover:text-[#B45309]"><Pencil className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {r.items.map((it) => (
                  <span key={`${it.sole_code}-${it.sole_color}-${it.size}`} className="text-[11px] font-mono px-2 py-1 rounded bg-[#FFFBF4] border border-[#DCCFB8] text-[#6B5B4A]">
                    {it.sole_code}{it.sole_color ? `·${it.sole_color}` : ""} · N°{it.size} · <b className="text-[#2B2118]">{it.qty}</b>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
